import React from 'react';
import { Helmet } from 'react-helmet';

const SEO = ({
  title = "Savory - Personalized Recipe Discovery",
  description = "Discover delicious, nutritionally balanced recipes tailored to your dietary preferences. Search by ingredients, nutrition goals, and dietary restrictions.",
  image = "/SavLogo.png",
  url = "https://savory.live",
  type = "website",
  keywords = "recipes, cooking, nutrition, healthy eating, meal planning, ingredients, dietary restrictions",
  author = "Savory Team",
  publishDate,
  modifiedDate,
  recipe = null, // For recipe-specific schema markup
  breadcrumb = null,
  canonical = null
}) => {
  const siteUrl = "https://savory.live";
  const fullUrl = url.startsWith('http') ? url : `${siteUrl}${url}`;
  const fullImageUrl = image.startsWith('http') ? image : `${siteUrl}${image}`;
  
  // Generate recipe schema markup
  const generateRecipeSchema = (recipeData) => {
    if (!recipeData) return null;
    
    const schema = {
      "@context": "https://schema.org",
      "@type": "Recipe",
      "name": recipeData.name,
      "description": recipeData.description || description,
      "image": recipeData.url || fullImageUrl,
      "author": {
        "@type": "Person",
        "name": recipeData.creator || author
      },
      "datePublished": recipeData.datePublished || publishDate,
      "dateModified": recipeData.dateModified || modifiedDate || new Date().toISOString(),
      "prepTime": recipeData.prepTime ? `PT${recipeData.prepTime}M` : undefined,
      "cookTime": recipeData.cookTime ? `PT${recipeData.cookTime}M` : undefined,
      "totalTime": recipeData.totalTime ? `PT${recipeData.totalTime}M` : undefined,
      "recipeYield": recipeData.servings || "4 servings",
      "recipeCategory": recipeData.category || "Main Course",
      "recipeCuisine": recipeData.cuisine || "International",
      "keywords": recipeData.tags ? recipeData.tags.join(", ") : keywords,
      "nutrition": {
        "@type": "NutritionInformation",
        "calories": recipeData.calories ? `${recipeData.calories} calories` : undefined,
        "proteinContent": recipeData.proteins ? `${recipeData.proteins}g` : undefined,
        "fatContent": recipeData.fats ? `${recipeData.fats}g` : undefined,
        "carbohydrateContent": recipeData.carbs ? `${recipeData.carbs}g` : undefined,
        "fiberContent": recipeData.fiber ? `${recipeData.fiber}g` : undefined,
        "sodiumContent": recipeData.sodium ? `${recipeData.sodium}mg` : undefined
      },
      "recipeIngredient": recipeData.ingredients || [],
      "recipeInstructions": recipeData.instructions ? recipeData.instructions.map(instruction => ({
        "@type": "HowToStep",
        "text": instruction
      })) : [],
      "aggregateRating": recipeData.rating ? {
        "@type": "AggregateRating",
        "ratingValue": recipeData.rating,
        "ratingCount": recipeData.reviewCount || 1
      } : undefined,
      "video": recipeData.videoUrl ? {
        "@type": "VideoObject",
        "name": recipeData.name,
        "description": recipeData.description || description,
        "thumbnailUrl": recipeData.url || fullImageUrl,
        "contentUrl": recipeData.videoUrl,
        "embedUrl": recipeData.videoUrl,
        "uploadDate": recipeData.datePublished || publishDate
      } : undefined
    };
    
    // Remove undefined fields
    Object.keys(schema).forEach(key => {
      if (schema[key] === undefined) {
        delete schema[key];
      }
    });
    
    return schema;
  };

  // Generate breadcrumb schema
  const generateBreadcrumbSchema = (breadcrumbData) => {
    if (!breadcrumbData) return null;
    
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbData.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": item.url.startsWith('http') ? item.url : `${siteUrl}${item.url}`
      }))
    };
  };

  // Generate organization schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Savory",
    "url": siteUrl,
    "logo": `${siteUrl}/SavLogo.png`,
    "description": "Your personal recipe discovery platform for delicious, nutritionally balanced recipes",
    "sameAs": [
      // Add social media URLs when available
    ]
  };

  // Generate website schema
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Savory",
    "url": siteUrl,
    "description": description,
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${siteUrl}/?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };

  const recipeSchema = generateRecipeSchema(recipe);
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumb);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonical || fullUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:site_name" content="Savory" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:image:alt" content={title} />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:creator" content="@SavoryApp" />
      <meta name="twitter:site" content="@SavoryApp" />
      
      {/* Additional Meta Tags */}
      {publishDate && <meta name="article:published_time" content={publishDate} />}
      {modifiedDate && <meta name="article:modified_time" content={modifiedDate} />}
      
      {/* Mobile Optimization */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      <meta name="format-detection" content="telephone=no" />
      
      {/* Performance and Security */}
      <meta http-equiv="X-UA-Compatible" content="IE=edge" />
      <meta name="referrer" content="no-referrer-when-downgrade" />
      
      {/* PWA Meta Tags */}
      <meta name="application-name" content="Savory" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="Savory" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="msapplication-TileColor" content="#FF6B6C" />
      <meta name="theme-color" content="#FF6B6C" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      
      {recipeSchema && (
        <script type="application/ld+json">
          {JSON.stringify(recipeSchema)}
        </script>
      )}
      
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO; 